/**
 * Pannello Contesto del Centro Ticket V3 (prototipo `Centro Ticket.dc.html`
 * L308-L411): sei card impilate — richiedente, proprieta, email originale,
 * trasferimenti, cronologia, altri ticket dell'associato.
 *
 * Veste v4 (T37, `Centro Ticket v4.dc.html` L519-L601): stesse card, ordinate
 * per frequenza d'uso — proprieta, richiedente, cronologia, trasferimenti,
 * email originale, altri ticket. Le card e le loro props non cambiano.
 *
 * «Dati della pratica» (27/10/2026): in entrambe le vesti subito sotto le
 * Proprietà, solo sui ticket del canale «Documento Unico» o con valori salvati.
 *
 * Due collocazioni (ruling 2 e 13):
 * - `contextMode='panel'` e viewport >= 1024px: terza colonna della pagina;
 * - `contextMode='drawer'`, oppure sotto i 1024px: cassetto laterale
 *   "Dettagli" (`S3Drawer`), che nell'area rappresentante e l'unica forma
 *   possibile — `.assoc-app .page` e largo al massimo 1180px.
 *
 * Il cassetto lo rende questo componente SOLO se riceve `onClose`. Senza
 * `onClose` si rende la sola pila di card, anche con `contextMode='drawer'`:
 * e il caso in cui il contenitore possiede gia il proprio `S3Drawer` e ci
 * passa il pannello come `contextSlot` (Task 10, e Task 13 se l'area
 * rappresentante terra il cassetto in `SupportTicketView`). I due rami si
 * escludono: chi passa `onClose` non deve avvolgere il pannello in un altro
 * cassetto, altrimenti se ne vedrebbero due.
 */
import React from 'react';
import RequesterCard from './RequesterCard';
import PropertiesCard from './PropertiesCard';
import DatiPraticaCard from './DatiPraticaCard';
import EmailCard from './EmailCard';
import TransfersCard from './TransfersCard';
import HistoryCard from './HistoryCard';
import RelatedCard from './RelatedCard';
import { S3Drawer } from './primitives';
import { useNarrow } from './useNarrow';
import { useSupportLook } from '../v4/lookContext';
import type {
  Candidate, GroupLite, OperatorSupportApi, TicketDetail,
} from '../../../services/supportApi';

export interface ContextPanelProps {
  ticket: TicketDetail;
  api: OperatorSupportApi;
  candidates: Candidate[];
  groups: GroupLite[];
  canHandle: boolean;
  canTransfer: boolean;
  /** Ricerca di operatori fuori dal canale (Task 26, solo admin col permesso). */
  canAssignExternal?: boolean;
  /** Solo il Centro Ticket admin: nell'area rappresentante non e mai esistita. */
  showRequesterCard?: boolean;
  /** `'drawer'` forza il cassetto a qualunque larghezza (area rappresentante). */
  contextMode?: 'panel' | 'drawer';
  /** Cassetto aperto. Ignorato in colonna. */
  open?: boolean;
  /** Chiusura del cassetto: senza questa il pannello non lo rende da solo. */
  onClose?: () => void;
  onChanged: () => void | Promise<void>;
  onOpenTransfer: (toGroupId: string) => void;
  onActionError: (e: unknown) => void;
  /** Apertura di un ticket correlato (querystring in admin, navigate lato rappresentante). */
  onOpenTicket?: (id: string) => void;
  /**
   * Card su cui aprire il cassetto. Il cassetto e una colonna lunga: chi lo
   * apre da "Cambia stato / priorità" deve trovarsi davanti le Proprieta, non
   * la card del richiedente.
   */
  initialFocusCard?: 'properties';
}

const ContextPanel: React.FC<ContextPanelProps> = ({
  ticket, api, candidates, groups, canHandle, canTransfer, canAssignExternal = false,
  showRequesterCard = false, contextMode = 'panel', open = false, onClose,
  onChanged, onOpenTransfer, onActionError, onOpenTicket, initialFocusCard,
}) => {
  const narrow = useNarrow();
  const look = useSupportLook();
  const propsRef = React.useRef<HTMLDivElement>(null);
  const inDrawer = contextMode === 'drawer' || narrow;
  const ownsDrawer = inDrawer && Boolean(onClose);
  // In colonna il pannello e sempre a schermo; nel cassetto i correlati si
  // caricano solo quando l'operatore lo apre davvero.
  const visible = ownsDrawer ? open : true;

  // Apertura del cassetto su una card precisa: si scorre DOPO il render, e
  // solo quando il cassetto e davvero aperto.
  React.useEffect(() => {
    if (!ownsDrawer || !open || initialFocusCard !== 'properties') return;
    propsRef.current?.scrollIntoView?.({ block: 'start' });
  }, [ownsDrawer, open, initialFocusCard]);

  const requester = showRequesterCard ? (
    <RequesterCard ticket={ticket} api={api} canHandle={canHandle} onChanged={onChanged} />
  ) : null;

  // Il div e solo un'ancora di scorrimento: non introduce impaginazione
  // propria (la pila usa `gap`, non i margini dei figli).
  const properties = (
    <div ref={propsRef}>
      <PropertiesCard
        ticket={ticket}
        api={api}
        candidates={candidates}
        groups={groups}
        canHandle={canHandle}
        canTransfer={canTransfer}
        canAssignExternal={canAssignExternal}
        onChanged={onChanged}
        onOpenTransfer={onOpenTransfer}
        onActionError={onActionError}
      />
    </div>
  );

  // «Dati della pratica» (canale Documento Unico): subito sotto le Proprietà in
  // entrambe le vesti; la card non si rende se il ticket non ha campi ne valori.
  const pratica = (
    <DatiPraticaCard
      ticket={ticket}
      api={api}
      canHandle={canHandle}
      onChanged={onChanged}
      onActionError={onActionError}
    />
  );

  const email = <EmailCard ticket={ticket} />;
  const transfers = <TransfersCard transfers={ticket.transfers} />;
  const history = <HistoryCard history={ticket.history} />;
  const related = (
    <RelatedCard
      ticketId={ticket._id}
      api={api}
      active={visible}
      onOpenTicket={onOpenTicket}
    />
  );

  const cards = look === 'v4' ? (
    <>
      {properties}
      {pratica}
      {requester}
      {history}
      {transfers}
      {email}
      {related}
    </>
  ) : (
    <>
      {requester}
      {properties}
      {pratica}
      {email}
      {transfers}
      {history}
      {related}
    </>
  );

  if (ownsDrawer) {
    return (
      <S3Drawer
        open={open}
        onClose={onClose!}
        title="Dettagli"
        className={look === 'v4' ? 's4-ctx-drawer' : undefined}
        headClassName={look === 'v4' ? 's4-ctx-drawer__head' : undefined}
      >
        {cards}
      </S3Drawer>
    );
  }

  return <div className="s3-ctx__stack">{cards}</div>;
};

export default ContextPanel;
